import { patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
  JobType,
  type PlacementCandidateMarketingLinkClicks,
  PlacementCandidateMarketingLinkClicksSchema,
  PlacementCandidateStatus,
  type PreferredCandidateArea,
  type SavedPayAndScheduleFormData,
  ShiftNameType,
} from "../types";
import { getPlacementCandidateQueryKey } from "./placementCandidate.helpers";

export interface UpdatePlacementCandidateParams {
  placementCandidateId: string;
  workerId: string;
  status?: PlacementCandidateStatus;
  jobTypesInterested?: JobType[];
  shiftTypesInterested?: ShiftNameType[];
  preferredAreas?: PreferredCandidateArea[];
  preferredPayRate?: {
    amountInMinorUnits: number;
    currencyCode: string;
  };
  currentYearsOfExperience?: number;
  marketingLinkClicks?: PlacementCandidateMarketingLinkClicks;
  yearsOfExperienceByQualification?: Record<string, number | undefined>;
  preferredDaysOfWeek?: DayOfTheWeek[];
  payAndScheduleFormData?: SavedPayAndScheduleFormData;
  initialOnboardingFlowCompleted?: boolean;
}

export const updatePlacementCandidateRequestSchema = z.object({
  data: z.object({
    attributes: z.object({
      status: z.nativeEnum(PlacementCandidateStatus).optional(),
      jobTypesInterested: z.array(z.nativeEnum(JobType)).optional(),
      shiftTypesInterested: z.array(z.nativeEnum(ShiftNameType)).optional(),
      preferredAreas: z
        .array(
          z.object({
            location: z
              .object({
                type: z.literal("Point"),
                coordinates: z.tuple([z.number(), z.number()]),
              })
              .optional(),
            address: z.object({
              city: z.string().optional(),
              country: z.string().optional(),
              countryCode: z.string().optional(),
              formatted: z.string().optional(),
              line1: z.string().optional(),
              postalCode: z.string().optional(),
              region: z.string().optional(),
              subpremise: z.string().optional(),
              state: z.string().optional(),
              stateCode: z.string().optional(),
              streetName: z.string().optional(),
              streetNumber: z.string().optional(),
              metropolitanStatisticalArea: z.string().optional(),
            }),
            distance: z.number(),
          })
        )
        .optional(),
      preferredPayRate: z
        .object({
          amountInMinorUnits: z.number(),
          currencyCode: z.string(),
        })
        .optional(),
      currentYearsOfExperience: z.number().optional(),
      marketingLinkClicks: PlacementCandidateMarketingLinkClicksSchema.optional(),
      yearsOfExperienceByQualification: z.record(z.string(), z.number().optional()).optional(),
      preferredDaysOfWeek: z.array(z.nativeEnum(DayOfTheWeek)).optional(),
      payAndScheduleFormData: z
        .object({
          benefits: z
            .object({
              combined: z.boolean().optional(),
              healthInsurance: z.boolean().optional(),
              paidTimeOff: z.boolean().optional(),
            })
            .optional(),
          jobType: z.nativeEnum(JobType).optional(),
        })
        .optional(),
      initialOnboardingFlowCompleted: z.boolean().optional(),
    }),
    id: z.string(),
    type: z.literal("placement-candidates"),
  }),
});

export function useUpdatePlacementCandidate(
  options: UseMutationOptions<string, Error, UpdatePlacementCandidateParams> = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const {
        placementCandidateId,
        status,
        jobTypesInterested,
        shiftTypesInterested,
        preferredAreas,
        preferredPayRate,
        currentYearsOfExperience,
        marketingLinkClicks,
        yearsOfExperienceByQualification,
        preferredDaysOfWeek,
        payAndScheduleFormData,
        initialOnboardingFlowCompleted,
      } = params;

      const response = await patch({
        url: `${environmentConfig.REACT_APP_API_MAIN_URL}/api/placement-candidates/${placementCandidateId}`,
        data: {
          data: {
            attributes: {
              status,
              jobTypesInterested,
              shiftTypesInterested,
              preferredAreas,
              preferredPayRate,
              currentYearsOfExperience,
              marketingLinkClicks,
              yearsOfExperienceByQualification,
              preferredDaysOfWeek,
              payAndScheduleFormData,
              initialOnboardingFlowCompleted,
            },
            id: placementCandidateId,
            type: "placement-candidates",
          },
        },
        requestSchema: updatePlacementCandidateRequestSchema,
        responseSchema: z.string(),
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // make sure we refetch the placement candidate whenever we update it
      void queryClient.refetchQueries({
        queryKey: getPlacementCandidateQueryKey(variables.workerId),
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_PLACEMENT_CANDIDATE_FAILURE,
      useErrorMessage: "Failed to update candidate profile",
    },
    ...options,
  });
}
